import { YouTubeChannelInsights } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import {
  GetCreatorInsightsResponse,
  SaveCreatorSetInput,
  SaveCreatorSetMemberInput,
  toCreatorSetMemberSnapshot,
  YouTubeCreatorSetWithMembers
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { YouTubeCreatorSetMember } from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';
import { FetchError } from '@src/utils/fetch-error';
import { MemberMetricsChange } from './creator-sets-constants';

interface SetChanges {
  name?: string;
  description?: string | null;
  members?: YouTubeCreatorSetMember[];
}

// A save body for `set` with `changes` applied. The member list is sent whole (save replaces it)
// and positions are renumbered from the list order, so a reorder is just a reordered array.
export const toSaveInput = (
  set: YouTubeCreatorSetWithMembers,
  changes: SetChanges = {}
): SaveCreatorSetInput => ({
  id: set.id,
  company: set.company,
  name: changes.name ?? set.name,
  description: changes.description === undefined ? set.description : changes.description,
  search_params: set.search_params,
  members: (changes.members ?? set.members).map(({ channel_id, insights }, position) => ({
    channel_id,
    position,
    insights
  }))
});

// New members from search-result channels, in the given order, numbered from `firstPosition`. Each
// snapshot comes from the full lookup when Google returned the channel (searches carry no audience
// data or top videos), otherwise from the search result itself.
export const toNewMembers = (
  searchChannels: YouTubeChannelInsights[],
  lookedUpById: Map<string, YouTubeChannelInsights>,
  snapshottedAt: string,
  firstPosition = 0
): SaveCreatorSetMemberInput[] =>
  searchChannels.map((channel, index) => {
    const { channelId } = channel.youtubeChannel;
    return {
      channel_id: channelId,
      position: firstPosition + index,
      insights: toCreatorSetMemberSnapshot(lookedUpById.get(channelId) ?? channel, snapshottedAt)
    };
  });

// A save body appending `newMembers` after the set's existing members.
export const toAddMembersInput = (
  set: YouTubeCreatorSetWithMembers,
  newMembers: SaveCreatorSetMemberInput[]
): SaveCreatorSetInput => {
  const input = toSaveInput(set);
  return { ...input, members: [...input.members, ...newMembers] };
};

// The channels not already in `set`; a set holds each creator once.
export const withoutMembersOf = (
  channels: YouTubeChannelInsights[],
  set?: YouTubeCreatorSetWithMembers
): YouTubeChannelInsights[] => {
  if (!set) return channels;
  const memberIds = new Set(set.members.map(member => member.channel_id));
  return channels.filter(channel => !memberIds.has(channel.youtubeChannel.channelId));
};

// Whether a live set already has this name. Matches the server's rule: names are unique per
// company after trimming, case-sensitively.
export const isSetNameTaken = (sets: YouTubeCreatorSetWithMembers[], name: string): boolean =>
  sets.some(set => set.name === name.trim());

// A new set with the same search and members. Keeps the existing snapshots rather than re-querying
// Google.
export const toDuplicateInput = (
  set: YouTubeCreatorSetWithMembers,
  name: string
): SaveCreatorSetInput => {
  const { id: _id, ...input } = toSaveInput(set, { name });
  return input;
};

// The members with the one at `index` moved `offset` places, or unchanged if that's off either end.
export const moveMember = <T>(members: T[], index: number, offset: number): T[] => {
  const target = index + offset;
  if (index < 0 || index >= members.length || target < 0 || target >= members.length) {
    return members;
  }
  const next = [...members];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
};

// Int64String counts to numbers. A missing or malformed count reads as undefined, never 0.
export const parseCount = (count?: string): number | undefined => {
  if (count === undefined || count === '') return undefined;
  const parsed = Number(count);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const formatCount = (count?: string): string =>
  parseCount(count)?.toLocaleString('en-US') ?? '—';

// Google sends a channel's audience breakdown only when the creator shares viewer data with it.
export const sharesViewerData = ({ insights }: YouTubeCreatorSetMember): boolean =>
  (insights.channel_audience_attributes ?? []).length > 0;

export const chunk = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_unused, i) =>
    items.slice(i * size, (i + 1) * size)
  );

export const getChannelsById = (
  responses: GetCreatorInsightsResponse[]
): Map<string, YouTubeChannelInsights> =>
  new Map(
    responses
      .flatMap(({ creatorInsights = [], localCreatorInsights = [] }) => [
        ...creatorInsights,
        ...localCreatorInsights
      ])
      .flatMap(creator => creator.creatorChannels ?? [])
      .map(channel => [channel.youtubeChannel.channelId, channel])
  );

// Old vs new snapshot for every member, in the set's order, for the user to review before saving.
export const buildMetricsChanges = (
  members: YouTubeCreatorSetMember[],
  channelsById: Map<string, YouTubeChannelInsights>,
  snapshottedAt: string
): MemberMetricsChange[] =>
  members.map(member => {
    const channel = channelsById.get(member.channel_id);
    return {
      channelId: member.channel_id,
      displayName: member.insights.display_name,
      previous: member.insights,
      next: channel ? toCreatorSetMemberSnapshot(channel, snapshottedAt) : undefined
    };
  });

// The members with each refreshed snapshot swapped in; members Google didn't return are unchanged.
export const applyMetricsChanges = (
  members: YouTubeCreatorSetMember[],
  changes: MemberMetricsChange[]
): YouTubeCreatorSetMember[] => {
  const nextById = new Map(changes.map(change => [change.channelId, change.next]));
  return members.map(member => {
    const next = nextById.get(member.channel_id);
    return next ? { ...member, insights: next } : member;
  });
};

// Epoch ms for formatLastEdited, which takes a number; the DB rows carry ISO strings.
export const toEpochMs = (timestamp: string): number => Date.parse(timestamp);

// A snapshot or add date as "Sep 23, 2026"; '—' for a missing or malformed timestamp.
export const formatSetDate = (timestamp?: string): string => {
  const epochMs = timestamp ? toEpochMs(timestamp) : NaN;
  if (!Number.isFinite(epochMs)) return '—';
  return new Date(epochMs).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// saveCreatorSet's 409, or the legacy 400, for a name another live set already uses.
// Its other 400s (members, search_params) aren't about the name, so they don't match.
export const isNameTakenError = (e: unknown): e is FetchError =>
  e instanceof FetchError &&
  (e.status === 409 || e.status === 400) &&
  /already exists/.test(e.message);
